import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Banner.css";
import "../styles/global.css";


/* ────────── Outline SVG Icons ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>
    }
  />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const ImageIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </>
    }
  />
);
const EditIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
      </>
    }
  />
);
const TrashIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
      </>
    }
  />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const TagIcon = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </>
    }
  />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bn-pagination">
      <div className="bn-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="bn-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="bn-pagination__pages">
        <button
          className="bn-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="bn-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`bn-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          className="bn-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Component ────────── */
const Banner = () => {
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [bannerType, setBannerType] = useState("banner");

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [link, setLink] = useState("");
  const [status, setStatus] = useState("Active");
  const [message, setMessage] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  // Bulk-selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) {
      setPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Banners ── */
  const fetchBanners = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/banners`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBanners(data.data);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching banners:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  /* ── Search filter (memoised) ── */
  const filteredBanners = useMemo(() => {
    if (!filterText) return banners;
    const ft = filterText.toLowerCase();
    return banners.filter(
      (cat) =>
        cat.name?.toLowerCase().includes(ft) ||
        cat.id?.toString().toLowerCase().includes(ft) ||
        cat.status?.toLowerCase().includes(ft)
    );
  }, [filterText, banners]);

  /* ── Reset to page 1 when filter changes ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  /* ── Clamp currentPage if out of range (e.g. after delete) ── */
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredBanners.length / perPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredBanners.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessageAlert("");
      setMessageType("");
    }, 4000);
  };

  /* ── Status toggle ── */
  const handleStatusChange = (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";

    showConfirm({
      title: "Update Status",
      message: `Are you sure you want to change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/banners/status/${id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus} successfully!`, "success");
              fetchBanners();
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error while updating status.", "danger"));
      },
    });
  };

  /* ── Add or Update Banner ── */
  const handleSaveBanner = (e) => {
    e.preventDefault();
    setMessage("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("desc", desc);
    formData.append("link", link);
    formData.append("is_home", 1);
    formData.append("banner_type", bannerType);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/banners/${editSlug}`
      : `${API_BASE_URL}api/banners`;

    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setName("");
          setDesc("");
          setLink("");
          setStatus("Active");
          setBannerType("banner");
          setImage(null);
          setPreview(null);
          setIsEditMode(false);
          fetchBanners();

          // Close modal first, then show success at page top
          setTimeout(() => {
            const modalEl = document.getElementById("addBannerModal");
            const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
            if (modalInstance) {
              modalInstance.hide();
            }
            document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("padding-right");

            // Show success message at page top after modal closes
            flashMessage(
              isEditMode ? "Banner updated successfully!" : "Banner added successfully!",
              "success"
            );
          }, 500);
        } else {
          flashMessage("Failed to save banner.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit Banner ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.id);
    setName(row.name);
    setBannerType(row.banner_type || "banner");
    setDesc(row.short_desc);
    setLink(row.link);
    setStatus(row.status);
    setPreview(
      row.image
        ? `${API_BASE_URL}public/banner/${row.image}`
        : `${API_BASE_URL}public/noimage.jpg`
    );
    setImage(null);

    const modalEl = document.getElementById("addBannerModal");
    const modal = new window.bootstrap.Modal(modalEl);
    modal.show();
  };

  /* ── Delete single ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Banner",
      message: "Are you sure you want to delete this banner? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/banners/destroy/${id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Banner deleted successfully!", "success");
              fetchBanners();
            } else {
              flashMessage("Failed to delete banner.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting banner.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;

    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected banner${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/banners/destroy/${row.id}`, {
              method: "GET",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(
              `${count} banner${count > 1 ? "s" : ""} deleted successfully!`,
              "success"
            );
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            fetchBanners();
          })
          .catch(() => flashMessage("Error deleting banners!", "danger"));
      },
    });
  };

  /* ── Open Add modal (reset state) ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setName("");
    setDesc("");
    setLink("");
    setStatus("Active");
    setBannerType("banner");
    setImage(null);
    setPreview(null);
  };

  /* ── Pagination helpers ── */
  const totalRows = filteredBanners.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredBanners.slice(start, start + perPage);
  }, [filteredBanners, currentPage, perPage]);

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => <span className="id-pill">#{row.id}</span>,
        width: "90px",
        sortable: true,
        selector: (row) => row.id,
      },
      {
        name: "Image",
        cell: (row) => (
          <img
            src={
              row.image
                ? `${API_BASE_URL}public/banner/${row.image}`
                : `${API_BASE_URL}public/noimage.jpg`
            }
            alt={row.name}
            className="row-thumb"
          />
        ),
        width: "90px",
      },
      {
        name: "Name",
        selector: (row) => row.name,
        cell: (row) => <span className="cell-name">{row.name}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Type",
        selector: (row) => row.banner_type,
        cell: (row) => (
          <span className="type-pill">{row.banner_type || "banner"}</span>
        ),
      },
      {
        name: "Status",
        cell: (row) => (
          <span
            onClick={() => handleStatusChange(row.id, row.status)}
            className={`status-pill ${
              row.status === "Active" ? "is-active" : "is-inactive"
            }`}
            title="Click to change status"
          >
            <span className="dot" />
            {row.status}
          </span>
        ),
      },
      {
        name: "Created At",
        selector: (row) => new Date(row.created_at).toLocaleDateString("en-IN"),
        cell: (row) => (
          <span className="cell-muted">
            {new Date(row.created_at).toLocaleDateString("en-IN")}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="row-actions">
            <button
              className="ico-btn edit"
              onClick={() => handleEdit(row)}
              title="Edit Banner"
            >
              <EditIcon size={16} />
            </button>
            <button
              className="ico-btn del"
              onClick={() => handleDelete(row.id)}
              title="Delete Banner"
            >
              <TrashIcon size={16} />
            </button>
          </div>
        ),
        width: "120px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
        "&:hover": { background: "#f6fcf9" },
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: {
      style: { paddingLeft: "16px", paddingRight: "16px" },
    },
    pagination: { style: { display: "none" } },
  };

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {loading && (
          <div className="bn-overlay">
            <div className="bn-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="banner-shell">

              {/* ── Page header ── */}
              <div className="banner-head">
                <div>
                  <h2>Banners</h2>
                  <p>Manage your site banners — add, edit, or organize them.</p>
                </div>
                <button
                  type="button"
                  className="add-btn"
                  data-bs-toggle="modal"
                  data-bs-target="#addBannerModal"
                  onClick={openAddModal}
                >
                  <PlusIcon size={18} />
                  Add Banner
                </button>
              </div>

              {/* ── Card ── */}
              <div className="banner-card">
                <div className="card-top">
                  <div className="search-box">
                    <SearchIcon size={18} />
                    <input
                      type="text"
                      placeholder="Search by name, id or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="total-badge">
                    <TagIcon size={16} />
                    Total:&nbsp;<b>{filteredBanners.length}</b>
                  </div>
                </div>

                {messagealert && (
                  <div className={`bn-alert ${messageType}`} role="alert">
                    <span>{messagealert}</span>
                    <button
                      type="button"
                      onClick={() => setMessageAlert("")}
                      aria-label="Close"
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                )}

                {selectedRows.length > 0 && (
                  <div className="bulk-bar">
                    <div className="count">
                      <span className="badge-num">{selectedRows.length}</span>
                      item{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="actions">
                      <button
                        className="bulk-btn ghost"
                        onClick={() => {
                          setSelectedRows([]);
                          setToggleCleared(!toggleCleared);
                        }}
                      >
                        Clear
                      </button>
                      <button
                        className="bulk-btn danger"
                        onClick={handleBulkDelete}
                      >
                        <TrashIcon size={14} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) =>
                    setSelectedRows(selectedRows)
                  }
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div
                      style={{
                        padding: "40px 20px",
                        color: "var(--bn-muted)",
                        textAlign: "center",
                      }}
                    >
                      No banners found.
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {filteredBanners.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          <div
            className="modal fade"
            id="addBannerModal"
            tabIndex="-1"
            aria-labelledby="addBannerModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <form id="addBannerForm" onSubmit={handleSaveBanner}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="addBannerModalLabel">
                      {isEditMode ? "Edit Banner" : "Add New Banner"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                  </div>

                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label htmlFor="bannerName" className="form-label">
                          Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bannerName"
                          placeholder="Banner name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </div>

                      <div className="col-12">
                        <label htmlFor="bannerDesc" className="form-label">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          id="bannerDesc"
                          placeholder="Banner Description"
                          value={desc || ""}
                          onChange={(e) => setDesc(e.target.value)}
                          required
                        />
                      </div>

                      <div className="col-12">
                        <label htmlFor="bannerLink" className="form-label">
                          Link
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bannerLink"
                          placeholder="Banner Link"
                          value={link}
                          onChange={(e) => setLink(e.target.value)}
                          required
                        />
                      </div>

                      <div className="col-12">
                        <label htmlFor="bannerImage" className="form-label">
                          Image
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="bannerImage"
                          accept="image/*"
                          onChange={(e) => setImage(e.target.files[0])}
                        />
                        {preview && (
                          <img
                            src={preview}
                            alt="Preview"
                            className="preview-thumb"
                          />
                        )}
                      </div>

                      <div className="col-6">
                        <label className="form-label">Banner Type</label>
                        <select
                          className="form-select"
                          value={bannerType}
                          onChange={(e) => setBannerType(e.target.value)}
                        >
                          <option value="banner">Banner</option>
                          <option value="menu">Menu</option>
                          <option value="bottom">Bottom</option>
                          <option value="hero">Hero Banner</option>
                          <option value="exclusive">Exclusive Discount</option>
                        </select>
                      </div>

                      <div className="col-6">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          id="bannerStatus"
                          name="status"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {isEditMode ? "Update Banner" : "Add Banner"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Banner;